/**
 * MifeLife v2 — Admin layout + 6 inner pages (dashboard, mentors, users,
 * sessions, applications, settings).
 *
 * All admin routes go through V2AdminLayout which gates on /v2/admin/whoami.
 * Non-admin → /app, unauthed → /giris.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, UserCheck, Calendar, FileText, Settings, Sparkles,
  LogOut, Loader2, Search, Bell, Check, X, Plus, Edit3, Trash2, ExternalLink,
  Undo2, Home, Wallet, TrendingUp, Star, Mail, Rocket, CheckCircle2, AlertTriangle, Scale, Banknote, Megaphone, Send,
} from 'lucide-react';
import { apiRequest, getToken, logout as clearAuth } from '../../../lib/utils';
import { PhotoUpload } from '../../../components/v2/MentorEditorFields';
import { toast } from 'sonner';

const NAV = [
  { to: '/admin',                 label: 'Dashboard',     icon: LayoutDashboard, end: true,  testid: 'adm-nav-dashboard' },
  { to: '/admin/mentorler',       label: 'Mentörler',     icon: UserCheck,        testid: 'adm-nav-mentors' },
  { to: '/admin/kullanicilar',    label: 'Kullanıcılar',  icon: Users,            testid: 'adm-nav-users' },
  { to: '/admin/rezervasyonlar',  label: 'Rezervasyonlar',icon: Calendar,         testid: 'adm-nav-sessions' },
  { to: '/admin/raporlar',        label: 'Gelir & Raporlar',icon: TrendingUp,     testid: 'adm-nav-reports' },
  { to: '/admin/onboarding',      label: 'Onboarding',    icon: Rocket,           testid: 'adm-nav-onboarding' },
  { to: '/admin/campaign',        label: 'Kampanya',      icon: Megaphone,        testid: 'adm-nav-campaign' },
  { to: '/admin/disputes',        label: 'İtirazlar',     icon: Scale,            testid: 'adm-nav-disputes' },
  { to: '/admin/payouts',         label: 'Ödemeler',      icon: Banknote,         testid: 'adm-nav-payouts' },
  { to: '/admin/mesajlar',        label: 'Mesajlar',      icon: Mail,             testid: 'adm-nav-contact' },
  { to: '/admin/basvurular',      label: 'Başvurular',    icon: FileText,         testid: 'adm-nav-applications' },
  { to: '/admin/ayarlar',         label: 'Ayarlar',       icon: Settings,         testid: 'adm-nav-settings' },
];

// ============================================================================
// LAYOUT
// ============================================================================
export function V2AdminLayout() {
  const nav = useNavigate();
  const [whoami, setWhoami] = useState(null);
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!getToken()) { nav('/giris?after=admin'); return; }
    apiRequest('/v2/admin/whoami')
      .then((r) => {
        if (!r?.is_admin) { nav('/app'); return; }
        setWhoami(r); setReady(true);
        // Admin pages must not be indexed.
        let m = document.querySelector('meta[name="robots"]');
        if (!m) { m = document.createElement('meta'); m.setAttribute('name','robots'); document.head.appendChild(m); }
        m.setAttribute('content', 'noindex,nofollow');
      })
      .catch(() => { nav('/giris?after=admin'); });
  }, [nav]);

  const logout = () => { clearAuth(); nav('/'); };

  if (!ready) {
    return <div className="min-h-screen bg-[#0a0a14] text-white flex items-center justify-center"><Loader2 className="w-7 h-7 text-violet-300 animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0a0a14] text-white antialiased flex" data-testid="adm-layout">
      {/* Sidebar */}
      <aside className={`fixed md:static inset-y-0 left-0 z-40 w-64 flex flex-col border-r border-white/[0.06] bg-[#08080F] transform ${open ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 transition-transform`}>
        <Link to="/admin" className="flex items-center gap-2 px-6 h-16 border-b border-white/[0.04]">
          <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-cyan-400 flex items-center justify-center"><Sparkles className="w-3.5 h-3.5 text-white" strokeWidth={2.4} /></span>
          <span className="text-sm font-semibold tracking-tight">MifeLife <span className="text-violet-300">Admin</span></span>
        </Link>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {NAV.map((it) => (
            <NavLink
              key={it.to} to={it.to} end={it.end} data-testid={it.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${isActive ? 'bg-violet-500/15 text-white border border-violet-400/30' : 'text-slate-400 hover:bg-white/[0.04] hover:text-white border border-transparent'}`}
            >
              <it.icon className="w-4 h-4" strokeWidth={1.8} /> {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/[0.04] space-y-1">
          <div className="px-3 mb-1 text-[11px] text-slate-500 truncate">{whoami?.email}</div>
          <Link to="/" className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-400 hover:bg-white/[0.04] hover:text-white transition" data-testid="adm-link-home"><Home className="w-3.5 h-3.5" /> Ana sayfaya dön</Link>
          <Link to="/app" className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-400 hover:bg-white/[0.04] hover:text-white transition" data-testid="adm-link-user-panel">← Kullanıcı paneli</Link>
          <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-400 hover:bg-white/[0.04] hover:text-white transition" data-testid="adm-logout"><LogOut className="w-3.5 h-3.5" /> Çıkış</button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/60 md:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <main className="flex-1 min-h-screen overflow-x-hidden">
        <Topbar onMenu={() => setOpen(true)} email={whoami?.email} />
        <div className="md:p-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

const Topbar = ({ onMenu, email }) => (
  <header className="h-14 border-b border-white/[0.06] bg-[#0a0a14]/80 backdrop-blur sticky top-0 z-20 flex items-center gap-3 px-4 sm:px-6" data-testid="adm-topbar">
    <button onClick={onMenu} className="md:hidden p-2 -ml-2 text-slate-300 hover:text-white" aria-label="menu">☰</button>
    <div className="hidden md:flex items-center gap-2 flex-1 max-w-md">
      <Search className="w-3.5 h-3.5 text-slate-500" />
      <input placeholder="Ara…" className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-slate-600" data-testid="adm-topbar-search" />
    </div>
    <div className="ml-auto flex items-center gap-3">
      <Bell className="w-4 h-4 text-slate-400" />
      <span className="hidden sm:inline text-xs text-slate-400 truncate max-w-[180px]">{email}</span>
    </div>
  </header>
);

// ============================================================================
// DASHBOARD
// ============================================================================
export function V2AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiRequest('/v2/admin/dashboard').then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    document.title = 'Admin Dashboard · MifeLife';
  }, []);

  if (loading || !data) return <Pad><Loader2 className="w-6 h-6 text-violet-300 animate-spin" /></Pad>;
  const s = data.stats || {};

  return (
    <Pad testid="adm-dashboard">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Genel bakış</h1>
      <p className="text-sm text-slate-400 mb-8">Platform sağlığı — bugün.</p>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="adm-stats-grid">
        <StatCard label="Toplam kullanıcı" value={s.users_total} testid="adm-stat-users" />
        <StatCard label="Onaylı mentör" value={s.mentors_approved} sub={`${s.mentors_pending} bekliyor`} testid="adm-stat-mentors" />
        <StatCard label="Bu ay rezervasyon" value={s.sessions_this_month} testid="adm-stat-sessions" />
        <StatCard label="Bu ay gelir" value={`₺${(s.revenue_this_month_try ?? 0).toLocaleString('tr-TR')}`} testid="adm-stat-revenue" />
        <StatCard label="Early Access" value={s.early_access_total ?? 0} sub="kurucu üye" testid="adm-stat-early-access" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-8">
        <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="adm-activity-chart">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-4">Son 7 gün aktivite</div>
          <ActivityChart buckets={data.activity_7d || []} />
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="adm-bell">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-3">Aksiyon gerekli</div>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center justify-between"><span className="text-slate-300">Bekleyen başvuru</span><Link to="/admin/basvurular" className="text-violet-300 font-medium tabular-nums" data-testid="adm-bell-applications">{s.applications_pending ?? 0}</Link></li>
            <li className="flex items-center justify-between"><span className="text-slate-300">Bekleyen mentör</span><Link to="/admin/mentorler?status=pending" className="text-violet-300 font-medium tabular-nums">{s.mentors_pending ?? 0}</Link></li>
          </ul>
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="adm-last-sessions">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Son rezervasyonlar</div>
          <Link to="/admin/rezervasyonlar" className="text-xs text-violet-300">Tümünü gör →</Link>
        </div>
        {(data.last_sessions || []).length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">Henüz rezervasyon yok.</div>
        ) : (
          <Table headers={['Mentör', 'Kullanıcı', 'Tarih', 'Tutar', 'Durum']}>
            {data.last_sessions.map((s) => (
              <tr key={s.id} className="border-t border-white/[0.04]">
                <Td>{s.mentor_name}</Td>
                <Td>{s.user_email || '—'}</Td>
                <Td>{new Date(s.scheduled_at).toLocaleString('tr-TR')}</Td>
                <Td>₺{(s.price_try ?? 0).toLocaleString('tr-TR')}</Td>
                <Td><StatusPill status={s.status} /></Td>
              </tr>
            ))}
          </Table>
        )}
      </div>
    </Pad>
  );
}

// Tiny svg bar chart — no external lib.
const ActivityChart = ({ buckets }) => {
  if (!buckets.length) return <div className="text-xs text-slate-500">veri yok</div>;
  const max = Math.max(1, ...buckets.flatMap((b) => [b.users, b.analyses, b.sessions]));
  const w = 100 / buckets.length;
  return (
    <svg viewBox="0 0 100 40" className="w-full h-40">
      {buckets.map((b, i) => (
        <g key={i} transform={`translate(${i * w}, 0)`}>
          <rect x={w * 0.10} y={40 - (b.users / max) * 38}     width={w * 0.22} height={(b.users / max) * 38}     fill="#8B5CF6" />
          <rect x={w * 0.36} y={40 - (b.analyses / max) * 38}  width={w * 0.22} height={(b.analyses / max) * 38}  fill="#06B6D4" />
          <rect x={w * 0.62} y={40 - (b.sessions / max) * 38}  width={w * 0.22} height={(b.sessions / max) * 38}  fill="#10B981" />
        </g>
      ))}
    </svg>
  );
};

// ============================================================================
// MENTORS
// ============================================================================
export function V2AdminMentorsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', specialty: '', language: '' });
  const [editing, setEditing] = useState(null);   // mentor obj or null
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    const qp = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => { if (v) qp.set(k, v); });
    apiRequest(`/v2/admin/mentors?${qp.toString()}`)
      .then((r) => setItems(r?.items || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); document.title = 'Mentörler · Admin · MifeLife'; /* eslint-disable-next-line */ }, [filter]);

  const onAction = async (id, kind) => {
    if (kind === 'delete' && !window.confirm('Silinsin mi? (soft-delete)')) return;
    const map = { approve: 'approve', reject: 'reject', delete: null };
    if (kind === 'delete') {
      await apiRequest(`/v2/admin/mentors/${id}`, { method: 'DELETE' });
    } else {
      let body = null;
      if (kind === 'reject') {
        const reason = window.prompt('Reddetme sebebi (mail mesajı):') || '';
        body = JSON.stringify({ reason });
      }
      await apiRequest(`/v2/admin/mentors/${id}/${map[kind]}`, { method: 'POST', body });
    }
    load();
  };

  return (
    <Pad testid="adm-mentors-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Mentör yönetimi</h1>
          <p className="text-sm text-slate-400 mt-1">Onaylama, düzenleme, manuel ekleme.</p>
        </div>
        <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-violet-500 text-white text-sm font-medium hover:bg-violet-400 transition" data-testid="adm-mentor-new"><Plus className="w-3.5 h-3.5" /> Yeni mentör</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <FilterSelect value={filter.status} onChange={(v) => setFilter((f) => ({...f, status: v}))} label="Durum" options={[['','Tümü'],['approved','Onaylı'],['pending','Bekliyor'],['rejected','Reddedilmiş']]} testid="adm-mentor-filter-status" />
        <FilterSelect value={filter.language} onChange={(v) => setFilter((f) => ({...f, language: v}))} label="Dil" options={[['','Tümü'],['tr','TR'],['en','EN'],['de','DE']]} testid="adm-mentor-filter-lang" />
      </div>

      {loading ? <Loader2 className="w-6 h-6 text-violet-300 animate-spin" /> : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-sm text-slate-400">Filtreye uyan mentör yok.</div>
      ) : (
        <Table headers={['Mentör', 'Uzmanlık', '₺/sa', 'Diller', 'Durum', 'Aksiyonlar']}>
          {items.map((m) => (
            <tr key={m.id} className="border-t border-white/[0.04]" data-testid={`adm-mentor-row-${m.id}`}>
              <Td>
                <div className="flex items-center gap-3">
                  <img src={m.photo_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                  <div>
                    <div className="text-sm">{m.name}</div>
                    <div className="text-xs text-slate-500">{m.title}</div>
                  </div>
                </div>
              </Td>
              <Td>{(m.specialties || []).slice(0,3).join(', ')}</Td>
              <Td className="tabular-nums">{m.hourly_rate_try}</Td>
              <Td className="uppercase">{(m.languages || []).join(', ')}</Td>
              <Td><StatusPill status={m.status} /></Td>
              <Td>
                <div className="flex gap-1.5">
                  {m.status !== 'approved' && <IconBtn title="Onayla" tone="emerald" onClick={() => onAction(m.id,'approve')} testid={`adm-mentor-approve-${m.id}`}><Check className="w-3 h-3" /></IconBtn>}
                  {m.status !== 'rejected' && <IconBtn title="Reddet" tone="rose" onClick={() => onAction(m.id,'reject')} testid={`adm-mentor-reject-${m.id}`}><X className="w-3 h-3" /></IconBtn>}
                  <IconBtn title="Düzenle" tone="slate" onClick={() => setEditing(m)} testid={`adm-mentor-edit-${m.id}`}><Edit3 className="w-3 h-3" /></IconBtn>
                  <IconBtn title="Sil" tone="slate" onClick={() => onAction(m.id,'delete')} testid={`adm-mentor-delete-${m.id}`}><Trash2 className="w-3 h-3" /></IconBtn>
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      )}

      {creating && <MentorFormModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <MentorFormModal mentor={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </Pad>
  );
}

const SPECIALTIES = ['Kariyer','İşletme','Yazılım','Finans','Dijital Pazarlama','Liderlik','Girişimcilik','Satış','UX/UI','Veri Bilimi','Ekip Yönetimi','Growth','Pazarlama'];

const MentorFormModal = ({ mentor, onClose, onSaved }) => {
  const isNew = !mentor;
  const [f, setF] = useState({
    name: mentor?.name || '', title: mentor?.title || '', bio_short: mentor?.bio_short || '', bio_full: mentor?.bio_full || '',
    photo_url: mentor?.photo_url || '', specialties: mentor?.specialties || [], languages: mentor?.languages || ['tr'],
    hourly_rate_try: mentor?.hourly_rate_try || 400, city: mentor?.city || '', linkedin_url: mentor?.linkedin_url || '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const toggle = (key, val) => setF((s) => ({ ...s, [key]: s[key].includes(val) ? s[key].filter((x) => x !== val) : [...s[key], val] }));
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const payload = { ...f, hourly_rate_try: parseInt(f.hourly_rate_try,10) };
      if (isNew) await apiRequest('/v2/admin/mentors', { method: 'POST', body: JSON.stringify(payload) });
      else      await apiRequest(`/v2/admin/mentors/${mentor.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      onSaved();
    } catch (e) { setErr(e?.message || 'Kaydedilemedi.'); } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title={isNew ? 'Yeni mentör' : 'Mentörü düzenle'} testid="adm-mentor-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="İsim"><input value={f.name} onChange={(e) => setF({...f, name: e.target.value})} className={INP} /></Field>
        <Field label="Unvan"><input value={f.title} onChange={(e) => setF({...f, title: e.target.value})} className={INP} /></Field>
      </div>
      <Field label="Profil fotoğrafı"><PhotoUpload value={f.photo_url} onChange={(v) => setF({ ...f, photo_url: v })} testidPrefix="adm-mentor-photo" /></Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Şehir"><input value={f.city} onChange={(e) => setF({...f, city: e.target.value})} className={INP} /></Field>
        <Field label="Saatlik (₺)"><input type="number" value={f.hourly_rate_try} onChange={(e) => setF({...f, hourly_rate_try: e.target.value})} className={INP} /></Field>
      </div>
      <Field label="LinkedIn URL"><input value={f.linkedin_url} onChange={(e) => setF({...f, linkedin_url: e.target.value})} className={INP} /></Field>
      <Field label="Kısa bio"><textarea rows={3} value={f.bio_short} onChange={(e) => setF({...f, bio_short: e.target.value})} className={TXT} /></Field>
      <Field label="Uzmanlık">
        <div className="flex flex-wrap gap-1.5">
          {SPECIALTIES.map((s) => (
            <button type="button" key={s} onClick={() => toggle('specialties', s)} className={`text-xs px-2.5 py-1 rounded-full border transition ${f.specialties.includes(s) ? 'bg-violet-500/20 border-violet-400/50 text-white' : 'border-white/10 text-slate-300'}`}>{s}</button>
          ))}
        </div>
      </Field>
      <Field label="Diller">
        <div className="flex gap-1.5">
          {[['tr','TR'],['en','EN'],['de','DE']].map(([code, lbl]) => (
            <button type="button" key={code} onClick={() => toggle('languages', code)} className={`text-xs px-3 py-1 rounded-full border transition ${f.languages.includes(code) ? 'bg-violet-500/20 border-violet-400/50 text-white' : 'border-white/10 text-slate-300'}`}>{lbl}</button>
          ))}
        </div>
      </Field>
      {err && <div className="text-sm text-rose-300">{err}</div>}
      <div className="flex justify-end gap-2 pt-3">
        <button onClick={onClose} className="h-10 px-4 rounded-lg border border-white/15 bg-white/[0.04] text-sm">Vazgeç</button>
        <button onClick={submit} disabled={busy} className="h-10 px-4 rounded-lg bg-violet-500 text-white text-sm font-medium disabled:opacity-50" data-testid="adm-mentor-form-save">{busy ? 'Kaydediliyor…' : 'Kaydet'}</button>
      </div>
    </Modal>
  );
};

// ============================================================================
// USERS
// ============================================================================
export function V2AdminUsersPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [plan, setPlan] = useState('');
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    const qp = new URLSearchParams(); if (q) qp.set('q', q); if (plan) qp.set('plan', plan);
    apiRequest(`/v2/admin/users?${qp.toString()}`).then((r) => setItems(r?.items || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); document.title = 'Kullanıcılar · Admin'; /* eslint-disable-next-line */ }, [plan]);

  return (
    <Pad testid="adm-users-page">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Kullanıcılar</h1>
      <p className="text-sm text-slate-400 mb-6">{items.length} kullanıcı listeleniyor.</p>
      <div className="flex flex-wrap gap-2 mb-5">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key==='Enter' && load()} placeholder="İsim/email ara…" className="h-9 px-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm min-w-[220px]" data-testid="adm-users-search" />
        <button onClick={load} className="h-9 px-3 rounded-lg border border-white/15 bg-white/[0.04] text-sm">Ara</button>
        <FilterSelect value={plan} onChange={setPlan} label="Plan" options={[['','Tümü'],['free','Free'],['pro','Pro'],['business','Business']]} testid="adm-users-plan" />
      </div>
      {loading ? <Loader2 className="w-6 h-6 text-violet-300 animate-spin" /> : (
        <Table headers={['Kullanıcı', 'Plan', 'Analiz', 'Seans', 'Kayıt']}>
          {items.map((u) => (
            <tr key={u.id} className="border-t border-white/[0.04]" data-testid={`adm-user-row-${u.id}`}>
              <Td>
                <div className="text-sm">{u.name || '—'}</div>
                <div className="text-xs text-slate-500">{u.email}</div>
              </Td>
              <Td><span className="text-xs px-2 py-0.5 rounded-full bg-white/[0.05] border border-white/10 uppercase">{u.subscription_tier || 'free'}</span></Td>
              <Td>{u.has_analysis ? '✓' : '—'}</Td>
              <Td className="tabular-nums">{u.session_count || 0}</Td>
              <Td>{u.created_at ? new Date(u.created_at).toLocaleDateString('tr-TR') : '—'}</Td>
            </tr>
          ))}
        </Table>
      )}
    </Pad>
  );
}

// ============================================================================
// SESSIONS
// ============================================================================
export function V2AdminSessionsPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    const qp = new URLSearchParams(); if (status) qp.set('status', status);
    apiRequest(`/v2/admin/sessions?${qp.toString()}`).then((r) => setItems(r?.items || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); document.title = 'Rezervasyonlar · Admin'; /* eslint-disable-next-line */ }, [status]);
  const cancel = async (id) => {
    if (!window.confirm('Seans iptal edilsin mi?')) return;
    await apiRequest(`/v2/admin/sessions/${id}/cancel`, { method: 'POST' });
    load();
  };
  const refund = async (s) => {
    const amount = (s.price_try ?? 0).toLocaleString('tr-TR');
    if (!window.confirm(`Bu seansa ₺${amount} iade işlemi başlatılacak. iyzico'dan müşteri kartına geri gönderilecek. Devam edilsin mi?`)) return;
    try {
      const res = await apiRequest(`/v2/admin/sessions/${s.id}/refund`, { method: 'POST' });
      alert(`İade başarılı: ₺${res.amount_try?.toLocaleString('tr-TR')} müşteriye iade edildi.`);
      load();
    } catch (e) {
      alert(`İade başarısız: ${e?.message || e}`);
    }
  };
  return (
    <Pad testid="adm-sessions-page">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Rezervasyonlar</h1>
      <p className="text-sm text-slate-400 mb-6">Mentör seansları ve ödeme durumları.</p>
      <div className="flex gap-2 mb-5">
        <FilterSelect value={status} onChange={setStatus} label="Durum" options={[['','Tümü'],['paid','Ödendi'],['pending_payment','Bekliyor'],['cancelled','İptal'],['refunded','İade edildi']]} testid="adm-sessions-status" />
      </div>
      {loading ? <Loader2 className="w-6 h-6 text-violet-300 animate-spin" /> : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-sm text-slate-400">Seans yok.</div>
      ) : (
        <Table headers={['Mentör', 'Kullanıcı', 'Tarih', 'Tutar', 'Durum', 'Jitsi', '']}>
          {items.map((s) => (
            <tr key={s.id} className="border-t border-white/[0.04]" data-testid={`adm-session-row-${s.id}`}>
              <Td>{s.mentor_name}</Td>
              <Td>{s.user_email || '—'}</Td>
              <Td>{new Date(s.scheduled_at).toLocaleString('tr-TR')}</Td>
              <Td className="tabular-nums">₺{(s.price_try ?? 0).toLocaleString('tr-TR')}</Td>
              <Td><StatusPill status={s.status} /></Td>
              <Td>{s.jitsi_link ? <a href={s.jitsi_link} target="_blank" rel="noopener noreferrer" className="text-violet-300 inline-flex items-center gap-1 text-xs">link <ExternalLink className="w-3 h-3" /></a> : '—'}</Td>
              <Td>
                <div className="flex items-center gap-1.5">
                  {s.status === 'paid' && (
                    <button
                      onClick={() => refund(s)}
                      className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-amber-500/15 border border-amber-400/30 text-amber-200 text-[11px] font-medium hover:bg-amber-500/25 transition"
                      title="Para iadesi (iyzico)"
                      data-testid={`adm-session-refund-${s.id}`}
                    >
                      <Undo2 className="w-3 h-3" /> İade
                    </button>
                  )}
                  {s.status !== 'cancelled' && s.status !== 'refunded' && (
                    <IconBtn tone="rose" onClick={() => cancel(s.id)} title="İptal" testid={`adm-session-cancel-${s.id}`}><X className="w-3 h-3" /></IconBtn>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      )}
    </Pad>
  );
}

// ============================================================================
// APPLICATIONS
// ============================================================================
export function V2AdminApplicationsPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    apiRequest(`/v2/mentor-applications/admin?status=${filter}`)
      .then((r) => { setItems(r?.items || []); setCounts(r?.counts || {}); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); document.title = 'Başvurular · Admin'; /* eslint-disable-next-line */ }, [filter]);

  const reject = async (id) => {
    if (!window.confirm('Reddedilsin mi?')) return;
    await apiRequest(`/v2/mentor-applications/admin/${id}/decide`, { method: 'POST', body: JSON.stringify({ decision: 'rejected' }) });
    load();
  };
  const promote = async (id) => {
    if (!window.confirm('Mentör olarak oluşturulsun mu?')) return;
    await apiRequest(`/v2/admin/applications/${id}/approve-as-mentor`, { method: 'POST' });
    load();
  };

  return (
    <Pad testid="adm-applications-page">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Mentör başvuruları</h1>
      <p className="text-sm text-slate-400 mb-6">Onayla → tek tıkla mentör olarak kataloga ekle.</p>
      <div className="flex gap-2 mb-5">
        {['pending','approved','rejected'].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] border transition ${filter === s ? 'bg-violet-500/15 border-violet-400/40 text-white' : 'border-white/10 text-slate-400 hover:text-white'}`} data-testid={`adm-app-filter-${s}`}>{s} ({counts[s] || 0})</button>
        ))}
      </div>
      {loading ? <Loader2 className="w-6 h-6 text-violet-300 animate-spin" /> : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-sm text-slate-400">Başvuru yok.</div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={`adm-app-${a.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                <div>
                  <div className="text-base font-semibold">{a.first_name} {a.last_name}</div>
                  <div className="text-xs text-slate-400">{a.email} · ₺{a.hourly_rate_try}/sa · {(a.languages || []).join(', ').toUpperCase()}</div>
                  {a.linkedin_url && <a href={a.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-xs text-violet-300 inline-flex items-center gap-1 mt-1">LinkedIn <ExternalLink className="w-3 h-3" /></a>}
                </div>
                {a.status === 'pending' ? (
                  <div className="flex gap-2">
                    <button onClick={() => promote(a.id)} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-emerald-500 text-white text-xs font-medium hover:bg-emerald-400 transition" data-testid={`adm-app-promote-${a.id}`}><Check className="w-3 h-3" /> Onayla & Mentör oluştur</button>
                    <button onClick={() => reject(a.id)} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-rose-500 text-white text-xs font-medium hover:bg-rose-400 transition" data-testid={`adm-app-reject-${a.id}`}><X className="w-3 h-3" /> Reddet</button>
                  </div>
                ) : <StatusPill status={a.status} />}
              </div>
              <div className="text-xs text-slate-400 mb-2">{(a.specialties || []).join(' · ')}</div>
              <p className="text-sm text-slate-200 leading-relaxed mb-1"><span className="text-slate-500 text-[10px] uppercase tracking-[0.18em] mr-2">Bio:</span>{a.bio_short}</p>
              <p className="text-sm text-slate-200 leading-relaxed"><span className="text-slate-500 text-[10px] uppercase tracking-[0.18em] mr-2">Neden:</span>{a.why_mifelife}</p>
            </div>
          ))}
        </div>
      )}
    </Pad>
  );
}

// ============================================================================
// SETTINGS
// ============================================================================
export function V2AdminSettingsPage() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  useEffect(() => {
    apiRequest('/v2/admin/settings').then(setS);
    document.title = 'Ayarlar · Admin';
  }, []);
  if (!s) return <Pad><Loader2 className="w-6 h-6 text-violet-300 animate-spin" /></Pad>;
  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        commission_pct: parseInt(s.commission_pct, 10),
        commission_pct_founder: parseInt(s.commission_pct_founder, 10),
        pro_price_try: parseInt(s.pro_price_try, 10),
        business_price_try: parseInt(s.business_price_try, 10),
        maintenance_mode: !!s.maintenance_mode,
        support_email: s.support_email,
        support_phone: s.support_phone,
      };
      const res = await apiRequest('/v2/admin/settings', { method: 'PATCH', body: JSON.stringify(patch) });
      setS(res); setSavedAt(new Date().toLocaleTimeString('tr-TR'));
    } finally { setSaving(false); }
  };
  const upd = (k, v) => setS({ ...s, [k]: v });
  return (
    <Pad testid="adm-settings-page">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Ayarlar</h1>
      <p className="text-sm text-slate-400 mb-6">Platform bazlı parametreler.</p>
      <div className="space-y-5 max-w-2xl">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Komisyon oranı (%)"><input type="number" value={s.commission_pct} onChange={(e) => upd('commission_pct', e.target.value)} className={INP} data-testid="adm-set-commission" /></Field>
          <Field label="Kurucu kohort komisyonu (%)"><input type="number" value={s.commission_pct_founder} onChange={(e) => upd('commission_pct_founder', e.target.value)} className={INP} data-testid="adm-set-commission-founder" /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Pro plan ₺/ay"><input type="number" value={s.pro_price_try} onChange={(e) => upd('pro_price_try', e.target.value)} className={INP} data-testid="adm-set-pro-price" /></Field>
          <Field label="Business plan ₺/ay"><input type="number" value={s.business_price_try} onChange={(e) => upd('business_price_try', e.target.value)} className={INP} data-testid="adm-set-business-price" /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Destek e-postası"><input value={s.support_email || ''} onChange={(e) => upd('support_email', e.target.value)} className={INP} /></Field>
          <Field label="Destek telefonu"><input value={s.support_phone || ''} onChange={(e) => upd('support_phone', e.target.value)} className={INP} /></Field>
        </div>
        <label className="flex items-center gap-2 cursor-pointer" data-testid="adm-set-maintenance">
          <input type="checkbox" checked={!!s.maintenance_mode} onChange={(e) => upd('maintenance_mode', e.target.checked)} />
          <span className="text-sm">Bakım modu (kullanıcıların erişimini kapat)</span>
        </label>
        <div className="flex items-center gap-3">
          <button onClick={save} disabled={saving} className="h-10 px-5 rounded-lg bg-violet-500 text-white text-sm font-medium hover:bg-violet-400 disabled:opacity-50" data-testid="adm-set-save">{saving ? 'Kaydediliyor…' : 'Kaydet'}</button>
          {savedAt && <span className="text-xs text-emerald-300">Kaydedildi · {savedAt}</span>}
        </div>
      </div>
    </Pad>
  );
}

// ============================================================================
// REPORTS — earnings ledger + mentor activity
// ============================================================================
const fmtTRY = (n) => `₺${Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`;

export function V2AdminReportsPage() {
  const [earnings, setEarnings] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('earnings');

  useEffect(() => {
    document.title = 'Gelir & Raporlar · Admin · MifeLife';
    Promise.all([
      apiRequest('/v2/admin/reports/earnings').catch(() => null),
      apiRequest('/v2/admin/reports/mentor-activity').catch(() => null),
    ]).then(([e, a]) => { setEarnings(e); setActivity(a); setLoading(false); });
  }, []);

  if (loading) return <Pad><Loader2 className="w-6 h-6 text-violet-300 animate-spin" /></Pad>;
  const p = earnings?.platform || {};

  return (
    <Pad testid="adm-reports-page">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Gelir & Raporlar</h1>
      <p className="text-sm text-slate-400 mb-8">Kazanç defteri, komisyon ve mentör aktivitesi.</p>

      {/* Platform KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="adm-reports-kpis">
        <StatCard label="Toplam ciro (gross)" value={fmtTRY(p.gross)} sub={`${p.count ?? 0} işlem`} testid="adm-rep-gross" />
        <StatCard label="Platform komisyonu" value={fmtTRY(p.commission)} sub="MifeLife geliri" testid="adm-rep-commission" />
        <StatCard label="Mentörlere net" value={fmtTRY(p.net_to_mentors)} sub="ödenecek toplam" testid="adm-rep-net" />
        <StatCard label="Bekleyen (clearing)" value={fmtTRY(p.pending_payout)} sub="henüz ödenebilir değil" testid="adm-rep-pending" />
        <StatCard label="Ödenebilir" value={fmtTRY(p.available_payout)} sub="payout'a hazır" testid="adm-rep-available" />
        <StatCard label="Ödenmiş" value={fmtTRY(p.paid_out)} sub={`${p.reversed_count ?? 0} iade`} testid="adm-rep-paidout" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mt-8 mb-5">
        <button onClick={() => setTab('earnings')} className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] border transition ${tab === 'earnings' ? 'bg-violet-500/15 border-violet-400/40 text-white' : 'border-white/10 text-slate-400 hover:text-white'}`} data-testid="adm-rep-tab-earnings"><Wallet className="w-3 h-3 inline mr-1.5" />Mentör kazançları</button>
        <button onClick={() => setTab('activity')} className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] border transition ${tab === 'activity' ? 'bg-violet-500/15 border-violet-400/40 text-white' : 'border-white/10 text-slate-400 hover:text-white'}`} data-testid="adm-rep-tab-activity"><UserCheck className="w-3 h-3 inline mr-1.5" />Mentör aktivitesi</button>
      </div>

      {tab === 'earnings' && (
        (earnings?.mentors || []).length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-sm text-slate-400" data-testid="adm-rep-earnings-empty">Henüz kazanç kaydı yok. İlk ödenen seansla burası dolacak.</div>
        ) : (
          <Table headers={['Mentör', 'Seans', 'Ciro', 'Komisyon', 'Net', 'Bekleyen', 'Ödenebilir']}>
            {earnings.mentors.map((m) => (
              <tr key={m.mentor_id} className="border-t border-white/[0.04]" data-testid={`adm-rep-earn-row-${m.mentor_id}`}>
                <Td>{m.mentor_name || m.mentor_id}</Td>
                <Td className="tabular-nums">{m.sessions}</Td>
                <Td className="tabular-nums">{fmtTRY(m.gross)}</Td>
                <Td className="tabular-nums text-amber-300">{fmtTRY(m.commission)}</Td>
                <Td className="tabular-nums text-emerald-300 font-medium">{fmtTRY(m.net)}</Td>
                <Td className="tabular-nums text-slate-400">{fmtTRY(m.pending_net)}</Td>
                <Td className="tabular-nums">{fmtTRY(m.available_net)}</Td>
              </tr>
            ))}
          </Table>
        )
      )}

      {tab === 'activity' && (
        <Table headers={['Mentör', 'Profil', 'Puan', 'Seans', 'Tamamlanan', 'Net kazanç', 'Durum']}>
          {(activity?.mentors || []).map((m) => (
            <tr key={m.mentor_id} className="border-t border-white/[0.04]" data-testid={`adm-rep-act-row-${m.mentor_id}`}>
              <Td>
                <div className="flex items-center gap-2.5">
                  <img src={m.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                  <div>
                    <div className="text-sm">{m.name}</div>
                    {m.is_founder_cohort && <div className="text-[10px] text-violet-300 uppercase tracking-[0.16em]">Kurucu kohort</div>}
                  </div>
                </div>
              </Td>
              <Td><CompletionMini value={m.profile_completion} /></Td>
              <Td><span className="inline-flex items-center gap-1 tabular-nums text-amber-200"><Star className="w-3 h-3 fill-amber-300 text-amber-300" />{m.rating?.toFixed(1)}{m.review_count > 0 && <span className="text-slate-500 text-xs">({m.review_count})</span>}</span></Td>
              <Td className="tabular-nums">{m.sessions_total}</Td>
              <Td className="tabular-nums text-emerald-300">{m.sessions_completed}</Td>
              <Td className="tabular-nums">{fmtTRY(m.net_earned_try)}</Td>
              <Td><StatusPill status={m.status} /></Td>
            </tr>
          ))}
        </Table>
      )}
    </Pad>
  );
}

const CompletionMini = ({ value = 0 }) => {
  const v = Math.max(0, Math.min(100, value || 0));
  const tone = v >= 80 ? 'bg-emerald-400' : v >= 50 ? 'bg-amber-400' : 'bg-rose-400';
  return (
    <div className="flex items-center gap-2 min-w-[90px]">
      <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden"><div className={`h-full ${tone}`} style={{ width: `${v}%` }} /></div>
      <span className="text-xs tabular-nums text-slate-400">{v}%</span>
    </div>
  );
};

// ============================================================================
// ONBOARDING & LAUNCH READINESS
// ============================================================================
const CLASS_PILL = {
  launch_ready: { label: 'Yayına hazır', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' },
  almost_ready: { label: 'Neredeyse', cls: 'bg-amber-500/15 text-amber-300 border-amber-400/30' },
  needs_work: { label: 'Eksik', cls: 'bg-rose-500/15 text-rose-300 border-rose-400/30' },
};
const ClassPill = ({ c }) => {
  const p = CLASS_PILL[c] || CLASS_PILL.needs_work;
  return <span className={`inline-block text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${p.cls}`}>{p.label}</span>;
};
const PowerBadge = ({ value = 0 }) => {
  const v = Math.max(0, Math.min(100, value || 0));
  const tone = v >= 80 ? 'text-emerald-300' : v >= 60 ? 'text-amber-300' : 'text-rose-300';
  return <span className={`tabular-nums font-semibold ${tone}`}>{v}</span>;
};

export function V2AdminOnboardingPage() {
  const [status, setStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Onboarding & Launch Readiness · Admin · MifeLife';
    Promise.all([
      apiRequest('/v2/admin/onboarding-status').catch(() => null),
      apiRequest('/v2/admin/launch-readiness').catch(() => null),
    ]).then(([s, r]) => { setStatus(s); setReport(r); setLoading(false); });
  }, []);

  if (loading) return <Pad><Loader2 className="w-6 h-6 text-violet-300 animate-spin" /></Pad>;
  const s = status || {};
  const r = report || {};
  const mentors = r.mentors || [];

  return (
    <Pad testid="adm-onboarding-page">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Onboarding & Launch Readiness</h1>
      <p className="text-sm text-slate-400 mb-8">İlk reklam bütçesi öncesi mentör arz tarafının ne kadarının gerçekten yayına hazır olduğunu gösterir.</p>

      {/* Onboarding status KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="adm-onboarding-kpis">
        <StatCard label="Toplam onaylı mentör" value={s.total ?? 0} sub="status: approved" testid="adm-onb-total" />
        <StatCard label="Yayına hazır" value={s.ready_to_publish ?? 0} sub="reklam trafiğine açılabilir" testid="adm-onb-ready" />
        <StatCard label="Eksik profille bekleyen" value={s.incomplete_waiting ?? 0} sub="henüz yayınlanamaz" testid="adm-onb-waiting" />
        <StatCard label="Neredeyse hazır" value={s.almost_ready ?? 0} sub="tamamlama ≥ %60" testid="adm-onb-almost" />
        <StatCard label="Ort. profil tamamlama" value={`%${s.avg_completion ?? 0}`} sub={`Ort. güç skoru ${s.avg_power_score ?? 0}`} testid="adm-onb-avgcompletion" />
        <StatCard label="Yayınlanabilir oran" value={`%${r.publishable_pct ?? 0}`} sub={`${r.publishable ?? 0} / ${r.total_approved ?? 0} mentör`} testid="adm-onb-publishable" />
      </div>

      {/* Launch readiness summary */}
      <div className="mt-8 grid lg:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-3">Sınıflandırma</div>
          <div className="space-y-2 text-sm">
            <Row3 label="Yayına hazır" value={r.classification_breakdown?.launch_ready ?? 0} tone="text-emerald-300" />
            <Row3 label="Neredeyse hazır" value={r.classification_breakdown?.almost_ready ?? 0} tone="text-amber-300" />
            <Row3 label="Eksik var" value={r.classification_breakdown?.needs_work ?? 0} tone="text-rose-300" />
          </div>
          <div className="mt-4 pt-3 border-t border-white/[0.06] text-sm">
            <Row3 label="Ortalama fiyat" value={fmtTRY(r.avg_price_try ?? 0)} />
            <Row3 label="Ortalama tamamlama" value={`%${r.avg_completion ?? 0}`} />
          </div>
        </div>
        <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="adm-onb-missing">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-3">En sık eksik alanlar</div>
          {(r.missing_breakdown || []).length === 0 ? (
            <div className="text-sm text-emerald-300">Tüm mentörler yayına hazır 🎉</div>
          ) : (
            <ul className="space-y-2">
              {r.missing_breakdown.map((mb) => (
                <li key={mb.field} className="flex items-center gap-3" data-testid={`adm-onb-missing-${mb.count}`}>
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                  <span className="text-sm text-slate-300 flex-1">{mb.field}</span>
                  <span className="text-xs tabular-nums text-slate-400">{mb.count} mentör</span>
                  <div className="w-24 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <div className="h-full bg-amber-400" style={{ width: `${Math.min(100, (mb.count / (r.total_approved || 1)) * 100)}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Per-mentor table */}
      <div className="mt-8 mb-3 text-[11px] uppercase tracking-[0.18em] text-slate-500">İlk 20 mentör · Launch Readiness</div>
      <Table headers={['Mentör', 'Profil', 'Güç Skoru', 'Durum', 'Eksik alanlar', 'Fiyat', 'Puan']}>
        {mentors.map((m) => (
          <tr key={m.id} className="border-t border-white/[0.04]" data-testid={`adm-onb-row-${m.id}`}>
            <Td>
              <div className="flex items-center gap-2">
                <span className="text-sm">{m.name}</span>
                {m.is_founder_cohort && <span className="text-[9px] text-violet-300 uppercase tracking-[0.16em]">Kurucu</span>}
              </div>
              <div className="text-[11px] text-slate-500">{m.title}</div>
            </Td>
            <Td><CompletionMini value={m.completion} /></Td>
            <Td><PowerBadge value={m.power_score} /></Td>
            <Td><ClassPill c={m.classification} /></Td>
            <Td>{m.missing_for_launch.length === 0
              ? <span className="inline-flex items-center gap-1 text-emerald-300 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> Tam</span>
              : <span className="text-[11px] text-amber-200/80">{m.missing_for_launch.join(', ')}</span>}</Td>
            <Td className="tabular-nums">{fmtTRY(m.hourly_rate_try)}</Td>
            <Td><span className="inline-flex items-center gap-1 tabular-nums text-amber-200"><Star className="w-3 h-3 fill-amber-300 text-amber-300" />{(m.rating || 0).toFixed(1)}</span></Td>
          </tr>
        ))}
      </Table>
    </Pad>
  );
}

const Row3 = ({ label, value, tone = 'text-white' }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-300">{label}</span>
    <span className={`tabular-nums font-medium ${tone}`}>{value}</span>
  </div>
);

// ============================================================================
// GROWTH CAMPAIGN
// ============================================================================
const CAMPAIGN_KIND_LABEL = { share_ready: 'Paylaşım (launch-ready)', completion_nudge: 'Profil tamamlama' };

export function V2AdminCampaignsPage() {
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  // launch form
  const [kind, setKind] = useState('share_ready');
  const [segment, setSegment] = useState('all');
  const [testMode, setTestMode] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      apiRequest('/v2/admin/campaigns/overview').catch(() => null),
      apiRequest('/v2/admin/campaigns').catch(() => ({ items: [] })),
      apiRequest('/v2/admin/campaigns/segments').catch(() => ({ segments: [] })),
    ]).then(([o, c, s]) => { setOverview(o); setCampaigns(c?.items || []); setSegments(s?.segments || []); setLoading(false); });
  };
  useEffect(() => { document.title = 'Kampanya · Admin · MifeLife'; load(); }, []);

  const launch = async () => {
    if (testMode && !testEmail.trim()) { toast.error('Test e-postası gir'); return; }
    setBusy(true);
    try {
      const c = await apiRequest('/v2/admin/campaigns/launch', { method: 'POST', body: JSON.stringify({
        kind, segment, test_mode: testMode, test_email: testEmail.trim() || null,
      })});
      toast.success(`Kampanya gönderildi: ${c.sent_count}/${c.recipient_count} başarılı`);
      load();
    } catch (e) { toast.error(e?.message || 'Kampanya başarısız'); }
    finally { setBusy(false); }
  };

  const openReport = async (id) => {
    try { setReport(await apiRequest(`/v2/admin/campaigns/${id}/report`)); }
    catch (e) { toast.error('Rapor alınamadı'); }
  };

  if (loading) return <Pad><Loader2 className="w-6 h-6 text-violet-300 animate-spin" /></Pad>;
  const o = overview || {};

  return (
    <Pad testid="adm-campaign-page">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Mentör Lansman Kampanyası</h1>
      <p className="text-sm text-slate-400 mb-8">İlk ücretli görüşmeleri organik mentör ağı üzerinden elde et. Domain doğrulanınca test modunu kapatıp gerçek lansmana geç.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label="Toplam onaylı" value={o.total_approved ?? 0} testid="adm-camp-total" />
        <StatCard label="Launch-ready" value={o.launch_ready ?? 0} sub="paylaşıma hazır" testid="adm-camp-ready" />
        <StatCard label="Profil tamamladı" value={o.completed_profile ?? 0} sub="%100" testid="adm-camp-completed" />
        <StatCard label="Kampanyaya dahil" value={o.in_share_campaign ?? 0} sub="paylaşım maili aldı" testid="adm-camp-included" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-10">
        {/* Launch form */}
        <div className="rounded-2xl border border-violet-400/20 bg-gradient-to-br from-violet-500/[0.06] to-fuchsia-500/[0.03] p-5" data-testid="adm-camp-launcher">
          <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Send className="w-4 h-4 text-fuchsia-300" /> Kampanya başlat</div>
          <label className="block text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">Tür</label>
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="w-full px-3 py-2 mb-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white focus:outline-none focus:border-violet-400/50" data-testid="adm-camp-kind">
            <option value="share_ready">Paylaşım maili — launch-ready mentörler</option>
            <option value="completion_nudge">Profil tamamlama — eksik mentörler</option>
          </select>
          <label className="block text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">Segment</label>
          <select value={segment} onChange={(e) => setSegment(e.target.value)} className="w-full px-3 py-2 mb-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white focus:outline-none focus:border-violet-400/50" data-testid="adm-camp-segment">
            <option value="all">Tüm segmentler</option>
            {segments.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <label className="flex items-center gap-2 mb-3 text-sm text-slate-200 cursor-pointer">
            <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} data-testid="adm-camp-testmode" />
            Test modu (tüm mailler tek adrese gider)
          </label>
          {testMode && (
            <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@adresin.com (Resend'de doğrulanmış)" className="w-full px-3 py-2 mb-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400/50" data-testid="adm-camp-testemail" />
          )}
          {!testMode && (
            <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-400/20 rounded-lg p-2.5 mb-3" data-testid="adm-camp-live-warning">
              ⚠️ Gerçek gönderim: mifelife.com Resend'de doğrulanmış olmalı, aksi halde mailler bounce olur.
            </div>
          )}
          <button onClick={launch} disabled={busy} className="w-full h-10 rounded-lg bg-violet-500 text-white text-sm font-semibold hover:bg-violet-400 disabled:opacity-50 transition flex items-center justify-center gap-2" data-testid="adm-camp-launch">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Kampanyayı gönder
          </button>
        </div>

        {/* Segment breakdown */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="adm-camp-segments">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-3">Segment dağılımı (launch-ready / toplam)</div>
          <ul className="space-y-2.5">
            {(o.segments || []).map((s) => (
              <li key={s.segment} className="flex items-center gap-3" data-testid={`adm-camp-seg-${s.segment}`}>
                <span className="text-sm text-slate-300 flex-1">{s.segment}</span>
                <span className="text-xs tabular-nums text-slate-400">{s.launch_ready}/{s.total}</span>
                <div className="w-28 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                  <div className="h-full bg-emerald-400" style={{ width: `${s.total ? (s.launch_ready / s.total) * 100 : 0}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Campaign history + funnel */}
      <div className="mb-3 text-[11px] uppercase tracking-[0.18em] text-slate-500">Kampanya geçmişi & funnel</div>
      <Table headers={['Tarih', 'Tür', 'Segment', 'Mod', 'Gönderildi', 'Başarısız', 'Funnel', '']}>
        {campaigns.map((c) => (
          <tr key={c.id} className="border-t border-white/[0.04]" data-testid={`adm-camp-row-${c.id}`}>
            <Td className="text-xs">{new Date(c.created_at).toLocaleDateString('tr-TR')}</Td>
            <Td className="text-xs">{CAMPAIGN_KIND_LABEL[c.kind] || c.kind}</Td>
            <Td className="text-xs">{c.segment}</Td>
            <Td>{c.test_mode ? <span className="text-[10px] text-amber-300">TEST</span> : <span className="text-[10px] text-emerald-300">CANLI</span>}</Td>
            <Td className="tabular-nums text-emerald-300">{c.sent_count}</Td>
            <Td className="tabular-nums text-rose-300">{c.failed_count}</Td>
            <Td><button onClick={() => openReport(c.id)} className="text-xs text-violet-300 hover:text-white transition" data-testid={`adm-camp-report-${c.id}`}>Funnel →</button></Td>
            <Td></Td>
          </tr>
        ))}
      </Table>

      {report && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="adm-camp-report-modal" onClick={() => setReport(null)}>
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#13131f] p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-1">Kampanya funnel raporu</h3>
            <p className="text-xs text-slate-400 mb-5">{CAMPAIGN_KIND_LABEL[report.campaign?.kind]} · {report.campaign?.segment} · {report.campaign?.test_mode ? 'Test' : 'Canlı'}</p>
            <div className="grid grid-cols-4 gap-2 mb-4">
              <FunnelStep label="Gönderildi" value={report.funnel.sent} testid="adm-camp-f-sent" />
              <FunnelStep label="Paylaşıldı" value={report.funnel.shared} testid="adm-camp-f-shared" />
              <FunnelStep label="Görüntülendi" value={report.funnel.profile_views} testid="adm-camp-f-views" />
              <FunnelStep label="Rezervasyon" value={report.funnel.bookings} testid="adm-camp-f-bookings" />
            </div>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-white/[0.06]">
              <table className="w-full text-sm">
                <tbody>
                  {(report.mentors || []).map((m) => (
                    <tr key={m.mentor_id} className="border-b border-white/[0.04]">
                      <td className="px-3 py-2 text-slate-300">{m.mentor_name}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{m.segment}</td>
                      <td className="px-3 py-2 text-xs">{m.shared ? <span className="text-emerald-300">paylaştı</span> : <span className="text-slate-600">—</span>}</td>
                      <td className="px-3 py-2 text-xs tabular-nums text-slate-400">{m.profile_views} görüntü</td>
                      <td className="px-3 py-2 text-xs tabular-nums text-slate-400">{m.bookings} rez.</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={() => setReport(null)} className="w-full mt-4 h-10 rounded-lg border border-white/15 text-slate-300 text-sm hover:bg-white/[0.04] transition">Kapat</button>
          </div>
        </div>
      )}
    </Pad>
  );
}

const FunnelStep = ({ label, value, testid }) => (
  <div className="rounded-xl border border-white/[0.06] bg-[#0a0a14] px-3 py-2.5 text-center" data-testid={testid}>
    <div className="text-xl font-semibold tabular-nums text-white">{value}</div>
    <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mt-0.5">{label}</div>
  </div>
);

// ============================================================================
// DISPUTES (itirazlar)
// ============================================================================
const REASON_LABEL = {
  mentor_no_show: 'Mentör no-show', mentee_no_show: 'Danışan no-show',
  technical_issue: 'Teknik sorun', quality: 'Kalite', not_as_described: 'Uyumsuz', other: 'Diğer',
};
const DISPUTE_OUTCOME_LABEL = {
  mentee_favor_full: 'Tam iade', partial: 'Kısmi iade', mentor_favor: 'Mentör lehine',
};

export function V2AdminDisputesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);   // dispute being resolved
  const [outcome, setOutcome] = useState('');
  const [ratio, setRatio] = useState(50);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    apiRequest('/v2/admin/disputes').then(setData).finally(() => setLoading(false));
  };
  useEffect(() => { document.title = 'İtirazlar · Admin · MifeLife'; load(); }, []);

  const resolve = async () => {
    if (!outcome) { toast.error('Çözüm türü seç'); return; }
    setBusy(true);
    try {
      const body = { outcome, note: note.trim() || null };
      if (outcome === 'partial') body.refund_ratio = Math.max(0.01, Math.min(0.99, ratio / 100));
      await apiRequest(`/v2/admin/disputes/${active.id}/resolve`, { method: 'POST', body: JSON.stringify(body) });
      toast.success('İtiraz çözüldü');
      setActive(null); setOutcome(''); setNote(''); setRatio(50);
      load();
    } catch (e) { toast.error(e?.message || 'Çözülemedi'); }
    finally { setBusy(false); }
  };

  if (loading) return <Pad><Loader2 className="w-6 h-6 text-violet-300 animate-spin" /></Pad>;
  const items = data?.items || [];
  const counts = data?.counts || {};

  return (
    <Pad testid="adm-disputes-page">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">İtirazlar (Dispute)</h1>
      <p className="text-sm text-slate-400 mb-8">Açık itirazlarda hakediş "incelemede" tutulur. Çözünce iade/serbest bırakma otomatik uygulanır.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatCard label="Açık itiraz" value={counts.open ?? 0} sub="müdahale bekliyor" testid="adm-disp-open" />
        <StatCard label="Çözülen" value={counts.resolved ?? 0} sub="toplam" testid="adm-disp-resolved" />
      </div>

      <Table headers={['Tarih', 'Mentör', 'Danışan', 'Sebep', 'Açan', 'Bedel', 'Durum', 'İşlem']}>
        {items.map((d) => (
          <tr key={d.id} className="border-t border-white/[0.04]" data-testid={`adm-disp-row-${d.id}`}>
            <Td className="text-xs">{new Date(d.created_at).toLocaleDateString('tr-TR')}</Td>
            <Td>{d.mentor_name}</Td>
            <Td>{d.user_name || '—'}</Td>
            <Td><span className="text-xs">{REASON_LABEL[d.reason_type] || d.reason_type}</span></Td>
            <Td className="text-xs">{d.opened_by_role === 'mentor' ? 'Mentör' : 'Danışan'}</Td>
            <Td className="tabular-nums">{fmtTRY(d.price_try)}</Td>
            <Td>{d.status === 'open'
              ? <span className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border bg-orange-500/15 text-orange-300 border-orange-400/30">Açık</span>
              : <span className="text-[10px] text-slate-400">{DISPUTE_OUTCOME_LABEL[d.outcome] || 'Çözüldü'}</span>}</Td>
            <Td>{d.status === 'open' && (
              <button onClick={() => setActive(d)} className="text-xs text-violet-300 hover:text-white transition" data-testid={`adm-disp-resolve-${d.id}`}>Çöz →</button>
            )}</Td>
          </tr>
        ))}
      </Table>

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="adm-disp-modal" onClick={() => setActive(null)}>
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#13131f] p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-1">İtirazı çöz</h3>
            <p className="text-xs text-slate-400 mb-4">{active.mentor_name} · {REASON_LABEL[active.reason_type]} · {fmtTRY(active.price_try)}</p>
            {active.message && <p className="text-[13px] text-slate-300 bg-white/[0.03] rounded-lg p-3 mb-4">"{active.message}"</p>}
            <label className="block text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">Çözüm</label>
            <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="w-full px-3 py-2 mb-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white focus:outline-none focus:border-violet-400/50" data-testid="adm-disp-outcome">
              <option value="">Seç…</option>
              <option value="mentee_favor_full">Danışan lehine — tam iade</option>
              <option value="partial">Kısmi iade</option>
              <option value="mentor_favor">Mentör lehine — serbest bırak (iade yok)</option>
            </select>
            {outcome === 'partial' && (
              <div className="mb-3" data-testid="adm-disp-ratio-wrap">
                <label className="block text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">İade oranı: %{ratio} ({fmtTRY(Math.round(active.price_try * ratio / 100))})</label>
                <input type="range" min={1} max={99} value={ratio} onChange={(e) => setRatio(Number(e.target.value))} className="w-full" data-testid="adm-disp-ratio" />
              </div>
            )}
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Karar notu (opsiyonel)…" className="w-full px-3 py-2 mb-4 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400/50 resize-none" data-testid="adm-disp-note" />
            <div className="flex gap-2">
              <button onClick={resolve} disabled={busy} className="flex-1 h-10 rounded-lg bg-violet-500 text-white text-sm font-semibold hover:bg-violet-400 disabled:opacity-50 transition flex items-center justify-center gap-2" data-testid="adm-disp-confirm">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Uygula
              </button>
              <button onClick={() => setActive(null)} className="h-10 px-4 rounded-lg border border-white/15 text-slate-300 text-sm hover:bg-white/[0.04] transition">İptal</button>
            </div>
          </div>
        </div>
      )}
    </Pad>
  );
}

// ============================================================================
// PAYOUTS (ödemeler)
// ============================================================================
export function V2AdminPayoutsPage() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      apiRequest('/v2/admin/payouts').catch(() => null),
      apiRequest('/v2/admin/payouts/history').catch(() => null),
    ]).then(([p, h]) => { setData(p); setHistory(h); setLoading(false); });
  };
  useEffect(() => { document.title = 'Ödemeler · Admin · MifeLife'; load(); }, []);

  const pay = async () => {
    if (reference.trim().length < 2) { toast.error('Dekont/referans gir'); return; }
    setBusy(true);
    try {
      await apiRequest('/v2/admin/payouts', { method: 'POST', body: JSON.stringify({
        mentor_id: active.mentor_id, reference: reference.trim(), note: note.trim() || null, override_hold: override,
      })});
      toast.success('Payout oluşturuldu');
      setActive(null); setReference(''); setNote(''); setOverride(false);
      load();
    } catch (e) { toast.error(e?.message || 'Payout başarısız'); }
    finally { setBusy(false); }
  };

  if (loading) return <Pad><Loader2 className="w-6 h-6 text-violet-300 animate-spin" /></Pad>;
  const s = data?.summary || {};
  const mentors = data?.mentors || [];

  return (
    <Pad testid="adm-payouts-page">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Ödemeler (Manuel Payout)</h1>
      <p className="text-sm text-slate-400 mb-8">Ödenebilir + dispute-free + IBAN-doğrulanmış + hold-geçmiş bakiyeler manuel olarak ödenir.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatCard label="Ödeme bekleyen mentör" value={s.payable_mentors ?? 0} testid="adm-pay-payable" />
        <StatCard label="Şu an uygun" value={s.eligible_now ?? 0} sub="ödemeye hazır" testid="adm-pay-eligible" />
        <StatCard label="Toplam ödenebilir" value={fmtTRY(s.total_available_net)} testid="adm-pay-total" />
        <StatCard label="Uygun bakiye" value={fmtTRY(s.eligible_available_net)} testid="adm-pay-eligible-amt" />
      </div>

      <Table headers={['Mentör', 'Ödenebilir', 'IBAN', 'Hold', 'Açık itiraz', 'Durum', 'İşlem']}>
        {mentors.map((m) => (
          <tr key={m.mentor_id} className="border-t border-white/[0.04]" data-testid={`adm-pay-row-${m.mentor_id}`}>
            <Td>{m.mentor_name}</Td>
            <Td className="tabular-nums text-emerald-300 font-medium">{fmtTRY(m.available_net)}</Td>
            <Td>{m.iban_valid
              ? <span className="text-xs text-emerald-300">{m.iban_masked}</span>
              : <span className="text-xs text-rose-300">{m.iban_set ? 'Geçersiz' : 'Yok'}</span>}</Td>
            <Td>{m.hold.active ? <span className="text-xs text-amber-300">Aktif</span> : <span className="text-xs text-slate-500">—</span>}</Td>
            <Td className="tabular-nums">{m.open_disputes}</Td>
            <Td>{m.eligible
              ? <span className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-400/30">Uygun</span>
              : <span className="text-[10px] text-amber-300/80" title={m.blockers.join(', ')}>Beklemede</span>}</Td>
            <Td><button onClick={() => { setActive(m); setOverride(false); }} disabled={m.available_net <= 0 || !m.iban_valid || m.open_disputes > 0} className="text-xs text-violet-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition" data-testid={`adm-pay-btn-${m.mentor_id}`}>Öde →</button></Td>
          </tr>
        ))}
      </Table>

      {history?.items?.length > 0 && (
        <>
          <div className="mt-10 mb-3 text-[11px] uppercase tracking-[0.18em] text-slate-500">Ödeme geçmişi · Toplam {fmtTRY(history.total_paid_out)}</div>
          <Table headers={['Tarih', 'Mentör', 'Tutar', 'Kayıt', 'Referans', 'Not']}>
            {history.items.map((p) => (
              <tr key={p.id} className="border-t border-white/[0.04]" data-testid={`adm-pay-hist-${p.id}`}>
                <Td className="text-xs">{new Date(p.created_at).toLocaleDateString('tr-TR')}</Td>
                <Td>{p.mentor_name}</Td>
                <Td className="tabular-nums text-emerald-300">{fmtTRY(p.amount_net_try)}</Td>
                <Td className="tabular-nums">{p.entry_count}</Td>
                <Td className="text-xs">{p.reference}</Td>
                <Td className="text-xs text-slate-400">{p.note || '—'}</Td>
              </tr>
            ))}
          </Table>
        </>
      )}

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="adm-pay-modal" onClick={() => setActive(null)}>
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#13131f] p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-1">Manuel payout</h3>
            <p className="text-xs text-slate-400 mb-4">{active.mentor_name} · {fmtTRY(active.available_net)} · IBAN {active.iban_masked}</p>
            {active.hold.active && (
              <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-400/20 rounded-lg p-3 mb-3" data-testid="adm-pay-hold-warning">
                Payout hold aktif: {active.hold.reasons.join(', ')}.
                <label className="flex items-center gap-2 mt-2 text-slate-200 cursor-pointer">
                  <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="adm-pay-override" /> Hold'u geç ve yine de öde
                </label>
              </div>
            )}
            <label className="block text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">Banka dekont / referans *</label>
            <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="TRF-2026-0612-001" className="w-full px-3 py-2 mb-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400/50" data-testid="adm-pay-reference" />
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Not (opsiyonel)…" className="w-full px-3 py-2 mb-4 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400/50 resize-none" data-testid="adm-pay-note" />
            <div className="flex gap-2">
              <button onClick={pay} disabled={busy || (active.hold.active && !override)} className="flex-1 h-10 rounded-lg bg-emerald-500 text-slate-900 text-sm font-semibold hover:bg-emerald-400 disabled:opacity-50 transition flex items-center justify-center gap-2" data-testid="adm-pay-confirm">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {fmtTRY(active.available_net)} öde
              </button>
              <button onClick={() => setActive(null)} className="h-10 px-4 rounded-lg border border-white/15 text-slate-300 text-sm hover:bg-white/[0.04] transition">İptal</button>
            </div>
          </div>
        </div>
      )}
    </Pad>
  );
}

// ============================================================================
// CONTACT MESSAGES
// ============================================================================
export function V2AdminContactPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = (status) => {
    setLoading(true);
    apiRequest(`/v2/admin/contact-messages${status ? `?status=${status}` : ''}`)
      .then(setData).catch(() => setData({ items: [], unread: 0 })).finally(() => setLoading(false));
  };
  useEffect(() => { document.title = 'Mesajlar · Admin · MifeLife'; load(filter); /* eslint-disable-next-line */ }, [filter]);

  const markRead = async (id) => {
    try { await apiRequest(`/v2/admin/contact-messages/${id}/read`, { method: 'POST' }); load(filter); } catch (e) { /* noop */ }
  };

  const CAT = { general: 'Genel', mentor: 'Mentör', partnership: 'İş birliği', support: 'Destek' };

  if (loading) return <Pad><Loader2 className="w-6 h-6 text-violet-300 animate-spin" /></Pad>;
  const items = data?.items || [];

  return (
    <Pad testid="adm-contact-page">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">İletişim Mesajları</h1>
        {data?.unread > 0 && <span className="text-xs px-2 py-1 rounded-full bg-violet-500/15 border border-violet-400/30 text-violet-200" data-testid="adm-contact-unread">{data.unread} okunmamış</span>}
      </div>
      <p className="text-sm text-slate-400 mb-6">İletişim formundan gelen mesajlar.</p>

      <div className="flex gap-2 mb-5">
        {[['', 'Tümü'], ['new', 'Okunmamış'], ['read', 'Okunmuş']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.16em] border transition ${filter === v ? 'bg-violet-500/15 border-violet-400/40 text-white' : 'border-white/10 text-slate-400 hover:text-white'}`} data-testid={`adm-contact-filter-${v || 'all'}`}>{l}</button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-sm text-slate-400" data-testid="adm-contact-empty">Henüz mesaj yok.</div>
      ) : (
        <div className="space-y-3" data-testid="adm-contact-list">
          {items.map((m) => (
            <div key={m.id} className={`rounded-2xl border p-5 ${m.status === 'new' ? 'border-violet-400/25 bg-violet-500/[0.04]' : 'border-white/10 bg-white/[0.02]'}`} data-testid={`adm-contact-msg-${m.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div>
                  <span className="text-sm font-semibold">{m.name}</span>
                  <a href={`mailto:${m.email}`} className="text-xs text-violet-300 ml-2 hover:text-violet-200">{m.email}</a>
                  <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 ml-2 px-2 py-0.5 rounded-full border border-white/10">{CAT[m.category] || m.category}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500">{(m.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                  {m.status === 'new' && <button onClick={() => markRead(m.id)} className="text-xs text-violet-300 hover:text-white transition" data-testid={`adm-contact-read-${m.id}`}>Okundu işaretle</button>}
                </div>
              </div>
              {m.subject && <div className="text-sm font-medium text-slate-200 mb-1">{m.subject}</div>}
              <p className="text-[13px] text-slate-300 leading-relaxed whitespace-pre-wrap">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </Pad>
  );
}

// ============================================================================
// Tiny primitives
// ============================================================================
// ============================================================================
// Tiny primitives
// ============================================================================
const INP = 'w-full h-10 px-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm focus:outline-none focus:border-violet-400/50 transition';
const TXT = 'w-full px-3 py-2 rounded-lg bg-[#0a0a14] border border-white/10 text-sm focus:outline-none focus:border-violet-400/50 transition resize-none';

const Pad = ({ children, testid }) => <div className="p-5 sm:p-8" data-testid={testid}>{children}</div>;
const Field = ({ label, children }) => <label className="block"><span className="block text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">{label}</span>{children}</label>;

const StatCard = ({ label, value, sub, testid }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">{label}</div>
    <div className="text-2xl sm:text-3xl font-semibold tabular-nums">{value ?? '—'}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const Table = ({ headers, children }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
          {headers.map((h) => <th key={h} className="text-left font-medium px-4 py-3">{h}</th>)}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);
const Td = ({ children, className = '' }) => <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;

const STATUS_TONES = {
  approved:        'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
  pending:         'bg-amber-500/15 text-amber-300 border-amber-400/30',
  pending_payment: 'bg-amber-500/15 text-amber-300 border-amber-400/30',
  rejected:        'bg-rose-500/15 text-rose-300 border-rose-400/30',
  paid:            'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
  cancelled:       'bg-slate-500/15 text-slate-400 border-slate-400/30',
  refunded:        'bg-amber-500/15 text-amber-300 border-amber-400/30',
};
const StatusPill = ({ status }) => (
  <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${STATUS_TONES[status] || 'bg-white/[0.05] text-slate-400 border-white/10'}`}>{status}</span>
);

const FilterSelect = ({ value, onChange, label, options, testid }) => (
  <label className="flex items-center gap-2 text-xs text-slate-400">
    <span className="uppercase tracking-[0.18em]">{label}:</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="h-9 px-2 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white" data-testid={testid}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

const ICON_TONES = {
  emerald: 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25',
  rose:    'bg-rose-500/15 text-rose-300 hover:bg-rose-500/25',
  slate:   'bg-white/[0.04] text-slate-300 hover:bg-white/[0.08]',
};
const IconBtn = ({ children, onClick, title, tone = 'slate', testid }) => (
  <button onClick={onClick} title={title} className={`p-1.5 rounded transition ${ICON_TONES[tone]}`} data-testid={testid}>{children}</button>
);

const Modal = ({ children, onClose, title, testid }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid={testid}>
    <div className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0F0F1A] p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold tracking-tight">{title}</h3>
        <button onClick={onClose} className="p-1 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);
